import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';

import {
  useGetAlarmsByAccountQuery,
  useGetAlarmsFeedByAccountQuery,
  useGetAlarmTypesQuery,
  useUpdateAlarmMutation,
} from '../../../api/recalc/endpoints/alarmsApiSlice';

import {
  useDeleteAlarmGroupMutation,
  useGetAlarmGroupsByAccountQuery,
  usePatchAlarmGroupMutation,
  usePostAlarmGroupMutation,
} from '../../../api/recalc/endpoints/alarmGroupsApiSlice';

import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';

import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import AlarmDataGrid from '@/features/alarm-hub/components/datagrid/AlarmDataGrid';

import CreateAlarmGroupDialog from '@/features/alarm-hub/components/dialog/CreateAlarmGroupDialog';
import EditAlarmGroupDialog from '@/features/alarm-hub/components/dialog/EditAlarmGroupDialog';
import AlarmSeverityAreaChart from '@/features/alarm-hub/components/highcharts/AlarmSeverityAreaChart';

const SEVERITY_COLORS = {
  Critical: '#c72929',
  Major: '#c72929',
  Medium: '#f47f1f',
  Minor: '#ffb300',
  Trivial: '#4cae4f',
  Information: '#1975d2',
  Unknown: 'transparent',
};

const SEVERITY_BUTTON_CONFIG = {
  Critical: {
    label: 'Critical',
    icon: ErrorRoundedIcon,
    color: SEVERITY_COLORS.Critical,
  },
  Major: {
    label: 'Major',
    icon: ErrorRoundedIcon,
    color: SEVERITY_COLORS.Major,
  },
  Medium: {
    label: 'Medium',
    icon: WarningRoundedIcon,
    color: SEVERITY_COLORS.Medium,
  },
  Minor: {
    label: 'Minor',
    icon: WarningRoundedIcon,
    color: SEVERITY_COLORS.Minor,
  },
  Information: {
    label: 'Information',
    icon: InfoRoundedIcon,
    color: SEVERITY_COLORS.Information,
  },
  Trivial: {
    label: 'Trivial',
    icon: InfoRoundedIcon,
    color: SEVERITY_COLORS.Trivial,
  },
};

const getAlertSeverity = (severity) => {
  switch (severity) {
    case 'Critical':
      return 'error';
    case 'Major':
      return 'error';
    case 'Medium':
      return 'warning';
    case 'Minor':
      return 'warning';
    case 'Trivial':
      return 'success';
    case 'Information':
      return 'info';
    default:
      return 'default';
  }
};

const AlarmHubPage = () => {
  const selectedAccount = useSelector(getSelectedAccount);
  const dispatch = useDispatch();

  const location = useLocation();

  const { data: alarmGroups } = useGetAlarmGroupsByAccountQuery(
    selectedAccount?.AccountId,
    {
      skip: !selectedAccount?.AccountId,
    },
  );
  const [postAlarmGroup] = usePostAlarmGroupMutation();
  const [patchAlarmGroup] = usePatchAlarmGroupMutation();
  const [deleteAlarmGroup] = useDeleteAlarmGroupMutation();
  const { data: alarmFeed } = useGetAlarmsFeedByAccountQuery(
    selectedAccount?.AccountId,
    {
      skip: !selectedAccount?.AccountId,
    },
  );
  const {
    data: alarms,
    isLoading: isLoadingAlarms,
    isFetching: isFetchingAlarms,
  } = useGetAlarmsByAccountQuery(selectedAccount?.AccountId, {
    skip: !selectedAccount?.AccountId,
  });
  const { data: alarmTypes } = useGetAlarmTypesQuery();

  const [updateAlarm] = useUpdateAlarmMutation();

  const [selectedSeverities, setSelectedSeverities] = useState({});
  const [selectedSeverity, setSelectedSeverity] = useState([]);

  const [selectedState, setSelectedState] = useState('all');
  const [selectedAlarmType, setSelectedAlarmType] = useState('all');

  const [openCreateAlarmGroupDialog, setOpenCreateAlarmGroupDialog] =
    useState(false);
  const [openEditAlarmGroupDialog, setOpenEditAlarmGroupDialog] =
    useState(false);

  const [selectedAlarmSelection, setSelectedAlarmSelection] = useState(null);

  const [dateRange, setDateRange] = useState([
    DateTime.utc().startOf('year'), // January 1st, 00:00 UTC
    DateTime.utc(), // current date and time UTC
  ]);

  const [useFeed, setUseFeed] = useState(true);

  const alarmFromNavigate = location.state?.alarm;

  useEffect(() => {
    if (alarmFromNavigate) {
      setSelectedAlarmSelection({
        alarm: alarmFromNavigate,
        associations: {
          Units: alarmFromNavigate.Associations.Units || [],
          Sensors: alarmFromNavigate.Associations.Sensors || [],
          Buildings: alarmFromNavigate.Associations.Buildings || [],
          JobSubscriptions:
            alarmFromNavigate.Associations.JobSubscriptions || [],
          Gateways: alarmFromNavigate.Associations.Gateways || [],
        },
      });
    }
  }, [alarmFromNavigate]);

  useEffect(() => {
    if (alarmFeed) {
      const initial = {};
      alarmFeed.forEach((a) => {
        const s = a.Severity || 'Unresolved';
        initial[s] = true; // default: all selected
      });
      setSelectedSeverities(initial);
    }
  }, [alarmFeed]);

  const toggleSeverity = (severity) => {
    setSelectedSeverities((prev) => ({
      ...prev,
      [severity]: !prev[severity],
    }));
  };

  const groupedCounts = useMemo(() => {
    if (!selectedAccount) return null;
    const counts = {
      Information: 0,
      Trivial: 0,
      Minor: 0,
      Medium: 0,
      Major: 0,
      Critical: 0,
    };
    const activeAlarmData = useFeed ? alarmFeed : alarms;

    if (activeAlarmData?.length > 0) {
      for (const alarm of activeAlarmData) {
        const created = DateTime.fromISO(alarm.Created, { zone: 'utc' });
        const severity = alarm.Severity;

        const inDateRange = created >= dateRange[0] && created <= dateRange[1];
        const matchesType =
          selectedAlarmType === 'all' || alarm.AlarmType === selectedAlarmType;
        const matchesState =
          selectedState === 'all' || alarm.State === selectedState;

        if (inDateRange && matchesType && matchesState) {
          if (counts[severity] !== undefined) counts[severity]++;
        }
      }
    }
    return counts;
  }, [
    selectedAccount,
    useFeed,
    alarmFeed,
    alarms,
    dateRange,
    selectedAlarmType,
    selectedState,
  ]);

  const filteredAlarms = useMemo(() => {
    const out = [];
    const activeAlarmData = useFeed ? alarmFeed : alarms;

    if (activeAlarmData?.length > 0) {
      for (const alarm of activeAlarmData) {
        const created = DateTime.fromISO(alarm.Created, { zone: 'utc' });
        const severity = alarm.Severity;

        const inDateRange = created >= dateRange[0] && created <= dateRange[1];
        const matchesType =
          selectedAlarmType === 'all' || alarm.AlarmType === selectedAlarmType;
        const matchesState =
          selectedState === 'all' || alarm.State === selectedState;
        const isVisibleSeverity = selectedSeverities[severity] !== false;

        if (inDateRange && matchesType && matchesState && isVisibleSeverity) {
          out.push(alarm);
        }
      }
    }
    return out;
  }, [
    alarmFeed,
    alarms,
    useFeed,
    selectedSeverities,
    selectedState,
    selectedAlarmType,
    dateRange,
  ]);

  useEffect(() => {
    if (!groupedCounts) return;
    setSelectedSeverities(() => {
      const next = {};
      Object.keys(SEVERITY_BUTTON_CONFIG).forEach((sev) => {
        const count = groupedCounts?.[sev] ?? 0;
        // If you truly want "more than one", use count > 1
        next[sev] = count > 0;
      });
      return next;
    });
  }, [useFeed, groupedCounts]); // include useFeed so this runs on source switch

  const handleAlarmUpdate = async (alarmId, updatedAlarm) => {
    try {
      await updateAlarm({ alarmId, alarm: updatedAlarm });
      dispatch(
        showSnackbar({
          message: 'Alarm updated successfully',
          severity: 'success',
        }),
      );
    } catch (error) {
      console.error('Error updating alarm:', error);
      dispatch(
        showSnackbar({
          message: 'Failed to update alarm',
          severity: 'error',
        }),
      );
    }
  };

  const handleAlarmDelete = async (alarmId) => {
    try {
      await deleteAlarmGroup(alarmId).unwrap();
      dispatch(
        showSnackbar({
          message: 'Alarm group deleted successfully',
          severity: 'success',
        }),
      );
    } catch (error) {
      console.error('Error deleting alarm group:', error);
      dispatch(
        showSnackbar({
          message: 'Failed to delete alarm group',
          severity: 'error',
        }),
      );
    }
  };

  const [showGraphs, setShowGraphs] = useState(true);

  const handleToggleGraphs = () => {
    setShowGraphs((prev) => !prev);
  };

  const handleAlarmGroupCreate = async (alarmGroup) => {
    try {
      await postAlarmGroup(alarmGroup).unwrap();
      dispatch(
        showSnackbar({
          message: 'Alarm group created successfully',
          severity: 'success',
        }),
      );
      setOpenCreateAlarmGroupDialog(false);
    } catch (error) {
      console.error('Error creating alarm group:', error);
      dispatch(
        showSnackbar({
          message: 'Failed to create alarm group',
          severity: 'error',
        }),
      );
      setOpenCreateAlarmGroupDialog(false);
    }
  };

  const handleAlarmGroupUpdate = async (alarmGroup) => {
    try {
      await patchAlarmGroup(alarmGroup).unwrap();
      dispatch(
        showSnackbar({
          message: 'Alarm group updated successfully',
          severity: 'success',
        }),
      );
      setOpenEditAlarmGroupDialog(false);
    } catch (error) {
      console.error('Error updating alarm group:', error);
      dispatch(
        showSnackbar({
          message: 'Failed to update alarm group',
          severity: 'error',
        }),
      );
      setOpenEditAlarmGroupDialog(false);
    }
  };

  return (
    <Container maxWidth="xxl">
      <Grid container spacing={2}>
        {Object.entries(SEVERITY_BUTTON_CONFIG).map(([key, config]) => {
          if (!config) return null;

          const { label, icon: Icon, color } = config;
          const count = groupedCounts?.[key] ?? 0;
          const isDisabled = count === 0;
          const isSelected = !isDisabled && selectedSeverities?.[key] !== false;

          return (
            <Grid key={key} lg={4} xl={2} item>
              <Alert
                variant="standard"
                severity={getAlertSeverity(key)}
                icon={Icon ? <Icon fontSize="inherit" /> : false}
                onClick={() => {
                  if (!isDisabled) toggleSeverity?.(key);
                }}
                // Accessibility hint
                role="button"
                aria-disabled={isDisabled}
                sx={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  borderLeft: '6px solid',
                  bgcolor: (theme) =>
                    isSelected ? 'none' : theme.palette.background.paper,
                  borderColor: isSelected ? color : 'divider',
                  '&:hover': {
                    borderColor: isDisabled ? 'divider' : getAlertSeverity(key),
                  },
                }}
              >
                <AlertTitle>{label}</AlertTitle>
                {count === 1 ? `${count} alarm` : `${count} alarmer`}
              </Alert>
            </Grid>
          );
        })}
        <Grid xs={12} md={12} item>
          <Card
            variant="outlined"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              maxHeight: 340,
            }}
          >
            {filteredAlarms && filteredAlarms.length > 0 ? (
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      height: '80vh',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <AlarmSeverityAreaChart
                  alarms={filteredAlarms}
                  // visibleSeverities={visibleSeverities}
                  onToggleSeverity={toggleSeverity}
                  handleToggleGraphs={handleToggleGraphs}
                  showGraphs={showGraphs}
                />
              </Suspense>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 150,
                }}
              >
                <Typography>
                  No alarms found for the selected filters.
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      <Suspense
        fallback={
          <Box
            sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <AlarmDataGrid
          alarms={filteredAlarms}
          alarmGroups={alarmGroups}
          severityFilter={selectedSeverity}
          onSeverityFilterChange={setSelectedSeverity}
          setSelectedAlarmSelection={setSelectedAlarmSelection}
          selectedAlarmSelection={selectedAlarmSelection}
          stateFilter={selectedState}
          onStateFilterChange={setSelectedState}
          handleAlarmUpdate={handleAlarmUpdate}
          alarmTypeFilter={selectedAlarmType}
          onAlarmTypeFilterChange={setSelectedAlarmType}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onToggleSeverity={toggleSeverity}
          setOpenCreateAlarmGroupDialog={setOpenCreateAlarmGroupDialog}
          setOpenEditAlarmGroupDialog={setOpenEditAlarmGroupDialog}
          groupedCounts={groupedCounts}
          showGraphs={showGraphs}
          isLoading={isLoadingAlarms || isFetchingAlarms}
          useFeed={useFeed}
          setUseFeed={setUseFeed}
        />
      </Suspense>

      {selectedAccount && (
        <Suspense
          fallback={
            <Box
              sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <CreateAlarmGroupDialog
            selectedAccount={selectedAccount}
            open={openCreateAlarmGroupDialog}
            onClose={() => setOpenCreateAlarmGroupDialog(false)}
            onCreate={handleAlarmGroupCreate}
            userOptions={selectedAccount?.UserRoles}
            alarmTypeOptions={alarmTypes}
          />
        </Suspense>
      )}

      {selectedAccount && (
        <Suspense
          fallback={
            <Box
              sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <EditAlarmGroupDialog
            selectedAccount={selectedAccount}
            open={openEditAlarmGroupDialog}
            onClose={() => setOpenEditAlarmGroupDialog(false)}
            openCreate={setOpenCreateAlarmGroupDialog}
            onUpdate={handleAlarmGroupUpdate}
            onDelete={handleAlarmDelete}
            userOptions={selectedAccount?.UserRoles}
            alarmTypeOptions={alarmTypes}
            alarmGroups={alarmGroups}
          />
        </Suspense>
      )}
    </Container>
  );
};

export default AlarmHubPage;
